import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

/**
 * Classroom Library — lessons mirrored from the Skool classrooms by skool-classroom-importer.
 * Lists and summaries are public; `skool_lesson_bodies` and the `skool-files` bucket are
 * gated by RLS (members + admins), which is why the body query is allowed to come back empty.
 */
export type ClassroomCourse = Tables<"skool_courses">;
export type ClassroomLesson = Tables<"skool_lessons">;
export type ClassroomFile = Tables<"skool_lesson_files">;

export const LESSON_LIST_COLUMNS =
  "id, course_id, slug, title, section, summary, skool_url, has_video, files_count, links_count, sort_order, last_changed_at";

export type LessonListRow = Pick<
  ClassroomLesson,
  | "id"
  | "course_id"
  | "slug"
  | "title"
  | "section"
  | "summary"
  | "skool_url"
  | "has_video"
  | "files_count"
  | "links_count"
  | "sort_order"
  | "last_changed_at"
>;

/** All courses + all active lessons, grouped client-side (two courses, ~100 lessons). */
export const useClassroom = () =>
  useQuery({
    queryKey: ["classroom", "index"],
    queryFn: async () => {
      const [{ data: courses, error: cErr }, { data: lessons, error: lErr }] = await Promise.all([
        supabase.from("skool_courses").select("*").order("sort_order", { ascending: true }).order("title"),
        supabase.from("skool_lessons").select(LESSON_LIST_COLUMNS).eq("status", "active").order("sort_order", { ascending: true }),
      ]);
      if (cErr) throw cErr;
      if (lErr) throw lErr;
      return { courses: (courses ?? []) as ClassroomCourse[], lessons: (lessons ?? []) as LessonListRow[] };
    },
    staleTime: 5 * 60_000,
  });

/** One lesson, its files, and (when RLS allows) its body. */
export const useClassroomLesson = (slug: string | undefined) =>
  useQuery({
    queryKey: ["classroom", "lesson", slug],
    enabled: !!slug,
    queryFn: async () => {
      const { data: lesson, error } = await supabase.from("skool_lessons").select("*").eq("slug", slug!).maybeSingle();
      if (error) throw error;
      if (!lesson) return null;
      const [{ data: course }, { data: files, error: fErr }, { data: body }] = await Promise.all([
        supabase.from("skool_courses").select("*").eq("id", lesson.course_id).maybeSingle(),
        supabase.from("skool_lesson_files").select("*").eq("lesson_id", lesson.id).order("sort_order", { ascending: true }),
        // Comes back null for non-members: RLS filters the row rather than erroring.
        supabase.from("skool_lesson_bodies").select("body_md").eq("lesson_id", lesson.id).maybeSingle(),
      ]);
      if (fErr) throw fErr;
      return {
        lesson: lesson as ClassroomLesson,
        course: (course ?? null) as ClassroomCourse | null,
        files: (files ?? []) as ClassroomFile[],
        bodyMd: body?.body_md ?? null,
      };
    },
  });

/** Short-lived URL for a mirrored file; only mints when the storage policy passes. */
export const signedClassroomFileUrl = async (storagePath: string, seconds = 120): Promise<string> => {
  const { data, error } = await supabase.storage.from("skool-files").createSignedUrl(storagePath, seconds);
  if (error || !data?.signedUrl) throw error ?? new Error("Could not sign the file URL");
  return data.signedUrl;
};

export const formatBytes = (n: number | null): string => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

/** Group active lessons by course, then by section, preserving Skool's order. */
export const groupLessons = (courses: ClassroomCourse[], lessons: LessonListRow[]) =>
  courses
    .map((course) => {
      const mine = lessons.filter((l) => l.course_id === course.id);
      const sections = new Map<string, LessonListRow[]>();
      for (const l of mine) {
        const key = l.section || "";
        if (!sections.has(key)) sections.set(key, []);
        sections.get(key)!.push(l);
      }
      return { course, sections: [...sections.entries()].map(([name, items]) => ({ name, items })), total: mine.length };
    })
    .filter((g) => g.total > 0);
